import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as templateService from '../services/templateService';
import * as templateUsageService from '../services/templateUsageService';
import * as creditService from '../services/creditService';
import * as templateProcessingService from '../services/templateProcessingService';
import { getFieldValue, filterResultByOutputFields, generateTemplatePrompt, parseTemplateResult } from '../utils/templateUtils';
import { getVariantFieldValue, setVariantFieldValue, updateVariantSkuFieldsInUI } from '../utils/productUtils';
import { calculateTemplateCredits } from '../utils/templateCreditCalculator';
import { useAuth } from './AuthContext';
import { useShop } from './ShopContext';
import { executeTemplatesV2 } from '../services/templateSystemV2';
import { WorkflowServiceV2 } from '../services/workflowServiceV2';
import { getAvailableAIModels } from '../services/aiModelService';
import { generateContent, deductCredits } from '../services/aiProviderService';

const TemplateContext = createContext();

export const useTemplateContext = () => useContext(TemplateContext);
export const useTemplate = useTemplateContext; // For backward compatibility

export const TemplateProvider = ({ children }) => {
  const [templates, setTemplates] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templateResults, setTemplateResults] = useState({});
  const [aiModels, setAIModels] = useState([]);
  const [editHistory, setEditHistory] = useState({
    past: [],
    present: {},
    future: []
  });
  const [products, setProducts] = useState([]);
  const [productEdits, setProductEdits] = useState({}); 
  const handleProductChangeRef = useRef(null);
  const handleOptionValueChangeRef = useRef(null);
  const handleVariantChangeRef = useRef(null);
  const { user } = useAuth();
  const { currentShop } = useShop();

  // Wait for auth to initialize
  useEffect(() => {
    if (user === null) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [user]);

  // Load AI models
  useEffect(() => {
    const loadAIModels = async () => {
      try {
        if (!user) return;
        const models = await getAvailableAIModels();
        setAIModels(models);
      } catch (error) {
        console.error('Error loading AI models:', error);
      }
    };

    loadAIModels();
  }, [user]);

  const validateTemplate = (template) => {
    const requiredFields = ['id', 'name', 'type', 'prompt', 'userId'];
    const missingFields = requiredFields.filter(field => !template[field]);
    
    if (missingFields.length > 0) {
      console.warn('Template missing required fields:', missingFields, template);
      return false;
    }
    
    // Ensure array fields are initialized
    if (!Array.isArray(template.inputFields)) {
      console.warn('Template missing inputFields array:', template);
      template.inputFields = [];
    }
    
    if (!Array.isArray(template.outputFields)) {
      console.warn('Template missing outputFields array:', template);
      template.outputFields = [];
    }
    
    if (!Array.isArray(template.inputMetafields)) {
      console.warn('Template missing inputMetafields array:', template);
      template.inputMetafields = [];
    }
    
    if (!Array.isArray(template.outputMetafields)) {
      console.warn('Template missing outputMetafields array:', template);
      template.outputMetafields = [];
    }
    
    // Initialize image template specific properties if missing
    if (template.type === 'image') {
      // Ensure boolean values are properly set for image templates
      // This is critical for checkbox UI components to work correctly
      template.sidebarOnly = Boolean(template.sidebarOnly);
      template.useFeaturedImage = Boolean(template.useFeaturedImage);
      
      // Check and initialize outputModes
      if (!template.outputModes || typeof template.outputModes !== 'object') {
        console.warn('Template missing outputModes object:', template);
        template.outputModes = {};
      }
      
      // Check and initialize outputMetafieldModes
      if (!template.outputMetafieldModes || typeof template.outputMetafieldModes !== 'object') {
        console.warn('Template missing outputMetafieldModes object:', template);
        template.outputMetafieldModes = {};
      }
      
      console.log('Validated image template properties:', {
        type: template.type,
        sidebarOnly: template.sidebarOnly,
        useFeaturedImage: template.useFeaturedImage,
        outputModes: template.outputModes
      });
    }
    
    return true;
  };

  const validateWorkflow = (workflow) => {
    const requiredFields = ['id', 'name', 'templates', 'userId'];
    const missingFields = requiredFields.filter(field => !workflow[field]);
    
    if (missingFields.length > 0) {
      console.warn('Workflow missing required fields:', missingFields, workflow);
      return false;
    }
    return true;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      if (!user) {
        setTemplates([]);
        setWorkflows([]);
        return;
      }

      console.log('Loading data for user:', user.uid);
      
      // Load templates
      const loadedTemplates = await templateService.getTemplatesByUserId(user.uid);
      const validTemplates = loadedTemplates.filter(validateTemplate);
      
      // Load workflows
      const loadedWorkflows = await templateService.getWorkflowsByUserId(user.uid);
      const validWorkflows = loadedWorkflows.filter(validateWorkflow);
      
      setTemplates(validTemplates);
      setWorkflows(validWorkflows);
    } catch (error) {
      console.error('Error loading data:', error);
      setTemplates([]);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    } else {
      setTemplates([]);
      setWorkflows([]);
      setLoading(false);
    }
  }, [user]);

  const addTemplate = async (templateData) => {
    if (!user) throw new Error('Must be logged in to add templates');

    try {
      const { 
        name, 
        type, 
        prompt, 
        metafields = [], 
        inputFields = [], 
        outputFields = [], 
        inputMetafields = [], 
        outputMetafields = [],
        // Image template specific properties
        sidebarOnly,
        useFeaturedImage,
        imageAnalysisMode,
        enableImagePlaceholders,
        outputModes,
        outputMetafieldModes,
        // Model information
        modelId,
        creditCostPerOutput
      } = templateData;
      
      if (!name || !type || !prompt) {
        throw new Error('Template must have name, type, and prompt');
      }
      
      const newTemplate = await templateService.saveTemplate(user.uid, {
        name,
        type,
        prompt,
        metafields,
        inputFields,
        outputFields,
        inputMetafields,
        outputMetafields,
        // Explicitly include image template properties
        sidebarOnly: sidebarOnly !== undefined ? sidebarOnly : false,
        useFeaturedImage: useFeaturedImage !== undefined ? useFeaturedImage : false,
        imageAnalysisMode: imageAnalysisMode || (useFeaturedImage ? 'featured' : 'manual'),
        enableImagePlaceholders: enableImagePlaceholders === true,
        outputModes: outputModes || {},
        outputMetafieldModes: outputMetafieldModes || {},
        // Include model information
        modelId: modelId || '',
        creditCostPerOutput: creditCostPerOutput || 1
      });
      
      if (!validateTemplate(newTemplate)) {
        throw new Error('Saved template is invalid');
      }
      
      setTemplates(prev => [...prev, newTemplate]);
      return newTemplate;
    } catch (error) {
      console.error('Error adding template:', error);
      throw error;
    }
  };

  const addWorkflow = async (workflowData) => {
    if (!user) throw new Error('Must be logged in to add workflows');

    try {
      const { name, templates: templateIds } = workflowData;
      if (!name || !templateIds?.length) {
        throw new Error('Workflow must have a name and at least one template');
      }

      const newWorkflow = await templateService.saveWorkflow(user.uid, {
        name,
        templates: templateIds
      });

      if (!validateWorkflow(newWorkflow)) {
        throw new Error('Saved workflow is invalid');
      }

      setWorkflows(prev => [...prev, newWorkflow]);
      return newWorkflow;
    } catch (error) {
      console.error('Error adding workflow:', error);
      throw error;
    }
  };

  const updateTemplate = async (templateId, updates) => {
    if (!user) throw new Error('Must be logged in to update templates');

    try {
      const { 
        name, 
        type, 
        prompt, 
        metafields, 
        inputFields, 
        outputFields, 
        inputMetafields, 
        outputMetafields,
        // Image template specific properties
        sidebarOnly,
        useFeaturedImage,
        outputModes
      } = updates;
      
      if (!name || !type || !prompt) {
        throw new Error('Template must have name, type, and prompt');
      }
      
      const updatedTemplate = await templateService.updateTemplate(templateId, {
        ...updates,
        userId: user.uid,
        inputFields: inputFields || [],
        outputFields: outputFields || [],
        inputMetafields: inputMetafields || [],
        outputMetafields: outputMetafields || [],
        // Explicitly include image template properties
        sidebarOnly: sidebarOnly !== undefined ? sidebarOnly : false,
        useFeaturedImage: useFeaturedImage !== undefined ? useFeaturedImage : false,
        imageAnalysisMode: updates.imageAnalysisMode || (useFeaturedImage ? 'featured' : 'manual'),
        enableImagePlaceholders: updates.enableImagePlaceholders === true,
        outputModes: outputModes || {}
      });
      
      if (!validateTemplate(updatedTemplate)) {
        throw new Error('Updated template is invalid');
      }
      
      setTemplates(prev =>
        prev.map(template =>
          template.id === templateId
            ? { ...template, ...updatedTemplate }
            : template
        )
      );
      return updatedTemplate;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!user) throw new Error('Must be logged in to delete templates');

    try {
      await templateService.deleteTemplate(templateId);
      setTemplates(prev => prev.filter(template => template.id !== templateId));
      
      // Also remove this template from any workflows that use it
      setWorkflows(prev => prev.map(workflow => ({
        ...workflow,
        templates: workflow.templates.filter(id => id !== templateId)
      })));
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!user) throw new Error('Must be logged in to delete workflows');

    try {
      await templateService.deleteWorkflow(workflowId);
      setWorkflows(prev => prev.filter(workflow => workflow.id !== workflowId));
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  };

  const updateWorkflow = async (workflowId, updates) => {
    if (!user) throw new Error('Must be logged in to update workflows');

    try {
      const { name, templates: templateIds } = updates;
      if (!name || !templateIds?.length) {
        throw new Error('Workflow must have a name and at least one template');
      }

      const updatedWorkflow = await templateService.updateWorkflow(workflowId, {
        ...updates,
        userId: user.uid
      });

      if (!validateWorkflow(updatedWorkflow)) {
        throw new Error('Updated workflow is invalid');
      }

      setWorkflows(prev =>
        prev.map(workflow =>
          workflow.id === workflowId
            ? { ...workflow, ...updatedWorkflow }
            : workflow
        )
      );
      return updatedWorkflow;
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  };

  const executeTemplate = async (templateId, productData) => {
    try {
      setLoading(true);
      
      // Get the template
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        throw new Error(`Template with ID ${templateId} not found`);
      }
      
      // Validate product data
      if (!productData || !productData.id) {
        throw new Error('Invalid product data');
      }
      
      // Get the AI model
      const model = aiModels.find(m => m.id === template.modelId);
      if (!model) {
        throw new Error(`AI model with ID ${template.modelId} not found`);
      }
      
      // Determine template type
      const isImageTemplate = template.type === 'image';
      const isToolTemplate = template.type === 'tool';
      
      // For tool templates, we'll execute the appropriate tool function directly
      if (isToolTemplate) {
        // No need to call AI API for tool templates
        // Parse the prompt to determine which tool to execute
        // Format: tool:toolName|parameters
        let result = {};
        
        if (!template.prompt.startsWith('tool:')) {
          throw new Error('Invalid tool template format. Prompt should start with "tool:"');
        }
        
        // Parse the tool name and parameters
        const [toolPart, paramsPart] = template.prompt.substring(5).split('|');
        const toolName = toolPart.trim();
        const params = paramsPart ? paramsPart.trim() : '';
        
        console.log('Executing tool template:', { toolName, params });
        
        // Execute the appropriate tool based on the tool name
        switch (toolName) {
          case 'image_alt_text':
            // Generate image alt text from product title
            const suffix = params || ' - product image';
            result.image_alt_text = productData.title + suffix;
            console.log('Tool executed: Generated image alt text', {
              productTitle: productData.title,
              suffix,
              result: result.image_alt_text
            });
            break;
            
          case 'vendor_name':
            // Set vendor name
            result.vendor = params;
            console.log('Tool executed: Set vendor name', {
              vendorName: params,
              result: result.vendor
            });
            break;
          
          case 'generate_skus':
            // Generate SKUs for variants
            const skuParams = {};
            if (params) {
              params.split('|').forEach(param => {
                const [key, value] = param.split('=');
                if (key && value !== undefined) {
                  skuParams[key.trim()] = value.trim();
                }
              });
            }
            
            // Create SKUs for all variants
            const prefix = skuParams.prefix || '';
            const skuSuffix = skuParams.suffix || '';
            
            // Create a slug from the product title
            const slugifiedTitle = productData.title
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/[\s_-]+/g, '-')
              .replace(/^-+|-+$/g, '');
            
            // Create SKUs for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                // Get variant options (e.g., 'Blue / XL')
                const optionValues = [];
                if (variant.option1) optionValues.push(variant.option1.toLowerCase().replace(/\s+/g, '-'));
                if (variant.option2) optionValues.push(variant.option2.toLowerCase().replace(/\s+/g, '-'));
                if (variant.option3) optionValues.push(variant.option3.toLowerCase().replace(/\s+/g, '-'));
                
                const optionPart = optionValues.length > 0 ? `-${optionValues.join('-')}` : '';
                const sku = `${prefix}${slugifiedTitle}${optionPart}${skuSuffix}`;
                
                result.variants[variant.id] = { sku };
              });
            }
            
            console.log('Tool executed: Generated SKUs', {
              prefix,
              suffix: skuSuffix,
              productTitle: productData.title,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'generate_handles':
            // Generate handle from title
            const handle = productData.title
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/[\s_-]+/g, '-')
              .replace(/^-+|-+$/g, '');
            
            result.handle = handle;
            console.log('Tool executed: Generated handle from title', {
              productTitle: productData.title,
              handle
            });
            break;
            
          case 'handle_prefix_suffix':
            // Add prefix/suffix to handle
            const handleParams = {};
            if (params) {
              params.split('|').forEach(param => {
                const [key, value] = param.split('=');
                if (key && value !== undefined) {
                  handleParams[key.trim()] = value.trim();
                }
              });
            }
            
            const handlePrefix = handleParams.prefix || '';
            const handleSuffix = handleParams.suffix || '';
            
            // Get current handle or generate from title if empty
            let currentHandle = productData.handle;
            if (!currentHandle) {
              currentHandle = productData.title
                .toLowerCase()
                .replace(/[^\w\s-]/g, '')
                .replace(/[\s_-]+/g, '-')
                .replace(/^-+|-+$/g, '');
            }
            
            result.handle = `${handlePrefix}${currentHandle}${handleSuffix}`;
            console.log('Tool executed: Added prefix/suffix to handle', {
              originalHandle: currentHandle,
              prefix: handlePrefix,
              suffix: handleSuffix,
              newHandle: result.handle
            });
            break;
            
          case 'update_prices':
            // Update prices based on parameters
            const priceParams = {};
            if (params) {
              params.split('|').forEach(param => {
                const [key, value] = param.split('=');
                if (key && value !== undefined) {
                  priceParams[key.trim()] = value.trim();
                }
              });
            }
            
            const amount = parseFloat(priceParams.amount || '0');
            const changeType = priceParams.type || 'percentage';
            const priceField = priceParams.field || 'price';
            
            // Create updates for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                const currentPrice = parseFloat(variant[priceField] || 0);
                let newPrice = currentPrice;
                
                if (changeType === 'percentage') {
                  // Percentage change
                  newPrice = currentPrice * (1 + amount / 100);
                } else {
                  // Fixed amount change
                  newPrice = currentPrice + amount;
                }
                
                // Ensure price is not negative
                newPrice = Math.max(0, newPrice);
                
                result.variants[variant.id] = { [priceField]: newPrice.toFixed(2) };
              });
            }
            
            console.log('Tool executed: Updated prices', {
              amount,
              changeType,
              priceField,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'sales_price_factor':
            // Apply sales price factor
            const factor = parseFloat(params || '1.0');
            
            // Create updates for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                const currentPrice = parseFloat(variant.price || 0);
                const newPrice = currentPrice * factor;
                
                result.variants[variant.id] = { price: newPrice.toFixed(2) };
              });
            }
            
            console.log('Tool executed: Applied sales price factor', {
              factor,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'round_prices':
            // Round prices to nearest dollar plus cents value
            const cents = parseFloat(params || '0.99');
            
            // Create updates for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                const currentPrice = parseFloat(variant.price || 0);
                const dollarPart = Math.floor(currentPrice);
                const newPrice = dollarPart + cents;
                
                result.variants[variant.id] = { price: newPrice.toFixed(2) };
              });
            }
            
            console.log('Tool executed: Rounded prices', {
              cents,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'compare_price_discount':
            // Set compare-at price discount
            const discountPercent = parseFloat(params || '20');
            
            // Create updates for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                const currentPrice = parseFloat(variant.price || 0);
                
                if (discountPercent >= 100) {
                  // If discount is 100% or more, remove compare-at price
                  result.variants[variant.id] = { compare_at_price: 0 };
                } else {
                  // Calculate compare-at price based on discount
                  const compareAtPrice = currentPrice / (1 - discountPercent / 100);
                  result.variants[variant.id] = { compare_at_price: compareAtPrice.toFixed(2) };
                }
              });
            }
            
            console.log('Tool executed: Set compare-at price discount', {
              discountPercent,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'clear_compare_prices':
            // Clear all compare-at prices
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                result.variants[variant.id] = { compare_at_price: 0 };
              });
            }
            
            console.log('Tool executed: Cleared compare-at prices', {
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          case 'set_inventory':
            // Set inventory quantity (with optional random range, tracking, and selling policy)
            const inventoryParams = {};
            if (params) {
              params.split('|').forEach(param => {
                const [key, value] = param.split('=');
                if (key && value !== undefined) {
                  inventoryParams[key.trim()] = value.trim();
                }
              });
            }
            
            const useRange = inventoryParams.useRange === 'yes';
            const quantityMin = parseInt(inventoryParams.quantityMin || '0');
            const quantityMax = parseInt(inventoryParams.quantityMax || '0');
            const fixedQuantity = parseInt(inventoryParams.quantity || '0');
            const ignoreZero = inventoryParams.ignoreZero === 'yes';
            const trackQuantity = inventoryParams.trackQuantity || 'unchanged'; // 'unchanged' | 'yes' | 'no'
            const continueSelling = inventoryParams.continueSelling || 'unchanged'; // 'unchanged' | 'yes' | 'no'
            
            // Create updates for each variant
            result.variants = {};
            if (productData.variants && productData.variants.length > 0) {
              productData.variants.forEach(variant => {
                const currentInventory = parseInt(variant.inventory_quantity || 0);
                
                // Skip variants with 0 inventory if ignoreZero is true
                if (ignoreZero && currentInventory === 0) {
                  return;
                }
                
                const variantUpdate = {};
                
                // Resolve quantity: fixed or random in range
                if (useRange) {
                  const safeMin = Math.min(quantityMin, quantityMax);
                  const safeMax = Math.max(quantityMin, quantityMax);
                  variantUpdate.inventory_quantity = Math.floor(Math.random() * (safeMax - safeMin + 1)) + safeMin;
                } else {
                  variantUpdate.inventory_quantity = fixedQuantity;
                }
                
                // Track quantity → sets inventory item "tracked" field
                if (trackQuantity !== 'unchanged') {
                  variantUpdate.tracked = trackQuantity === 'yes';
                }
                
                // Continue selling → sets variant inventoryPolicy (CONTINUE / DENY)
                if (continueSelling !== 'unchanged') {
                  variantUpdate.inventory_policy = continueSelling === 'yes' ? 'CONTINUE' : 'DENY';
                }
                
                result.variants[variant.id] = variantUpdate;
              });
            }
            
            console.log('Tool executed: Set inventory quantity', {
              useRange,
              quantityMin,
              quantityMax,
              fixedQuantity,
              ignoreZero,
              trackQuantity,
              continueSelling,
              variantCount: Object.keys(result.variants || {}).length
            });
            break;
            
          default:
            throw new Error(`Unknown tool: ${toolName}`);
        
        }
        
        // Update template results
        setTemplateResults(prev => ({
          ...prev,
          [templateId]: {
            ...prev[templateId],
            [productData.id]: result
          }
        }));
        
        // Log template usage (no credits deducted)
        await templateUsageService.recordTemplateUsage(user.uid, templateId, currentShop?.id);
        
        return result;
      }
      
      // For AI templates, build the prompt
      let prompt = template.prompt;
      
      // Replace placeholders with actual values
      template.inputFields.forEach(field => {
        const value = getFieldValue(productData, field);
        if (value !== undefined) {
          prompt = prompt.replace(new RegExp(`\\{${field}\\}`, 'g'), value);
        }
      });
      
      // Replace metafield placeholders
      if (template.inputMetafields && template.inputMetafields.length > 0) {
        template.inputMetafields.forEach(metafield => {
          const key = `${metafield.namespace}.${metafield.key}`;
          const value = productData.metafields?.[key] || '';
          prompt = prompt.replace(new RegExp(`\\{metafield\\.${key}\\}`, 'g'), value);
        });
      }
      
      // Prepare system message based on template type
      let systemMessage = 'You are a helpful assistant that generates product content based on the provided information. Respond with valid JSON only.';
      
      if (isImageTemplate) {
        systemMessage = 'You are an AI assistant that generates product content for an e-commerce platform. The image provided shows a product — which could be clothing, electronics, accessories, or another item. Focus only on visible features like shape, color, material, and likely use. Keep the output concise, factual, and avoid guessing things that are not visible. Respond with valid JSON only.';
      }
      
      // Call the AI provider
      const requestData = {
        messages: [
          {
            role: 'system',
            content: systemMessage
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: model.temperature || 0.7,
        max_tokens: model.maxTokens || 2000
      };
      
      // Add images for image templates
      if (isImageTemplate && template.selectedImage) {
        requestData.images = [template.selectedImage];
      }
      
      // Deduct credits (only for non-tool templates)
      if (!isToolTemplate) {
        const outputCount = template.outputFields.length;
        await deductCredits(user.uid, template.modelId, outputCount);
      }
      
      // Generate content
      const response = await generateContent(template.modelId, requestData);
      
      // Parse the response
      const content = response.choices[0].message.content;
      let result;
      
      try {
        result = JSON.parse(content);
      } catch (error) {
        console.error('Error parsing AI response:', error);
        throw new Error('Invalid response format from AI provider');
      }
      
      // Process the result based on template type
      if (isImageTemplate && template.outputModes) {
        // Apply output modes for image templates (exchange vs. add vs. sidebar)
        Object.entries(template.outputModes).forEach(([field, mode]) => {
          if (mode === 'add' && result[field] && productData[field]) {
            // Append the generated content to the existing content
            result[field] = `${productData[field]}\n\n${result[field]}`;
          }
          
          // For sidebar mode, we'll keep the result in the templateResults state
          // but mark it so it doesn't get applied to the product data
          if (mode === 'sidebar' && result[field]) {
            // Add a special flag to indicate this field should only be shown in sidebar
            result[`${field}_sidebar_only`] = true;
          }
        });
      }
      
      // Update template results
      setTemplateResults(prev => ({
        ...prev,
        [templateId]: {
          ...prev[templateId],
          [productData.id]: result
        }
      }));
      
      // Log template usage
      await templateUsageService.recordTemplateUsage(user.uid, templateId, currentShop?.id);
      
      return result;
    } catch (error) {
      console.error('Error executing template:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const executeTemplates = async (templateIds, products, onProgress = null) => {
    console.log('executeTemplates called with:', { templateIds, productCount: products.length });
    
    if (!templateIds || !products) {
      console.error('Missing required parameters for executeTemplates');
      return { error: 'Missing required parameters for executeTemplates' };
    }
    
    if (!user?.uid) {
      console.error('User not authenticated');
      return { error: 'User not authenticated' };
    }
    
    try {
      setLoading(true);
      
      // CRITICAL: Pre-execution credits check
      console.log('🔍 Starting pre-execution credits check...');
      
      // Calculate total required credits based on templates and products
      let totalRequiredCredits = 0;
      const templateDetails = [];
      
      for (const templateId of templateIds) {
        const template = templates.find(t => t.id === templateId);
        if (!template) {
          console.error(`Template not found: ${templateId}`);
          setLoading(false);
          return { error: `Template not found: ${templateId}` };
        }
        
        // Calculate credits per template with smart field-based pricing
        const aiModelCost = template.creditCostPerOutput || 1; // Base cost from AI model
        const creditsPerProduct = calculateTemplateCredits(template, aiModelCost); // Smart calculation with field complexity
        const templateCredits = creditsPerProduct * products.length;
        
        totalRequiredCredits += templateCredits;
        templateDetails.push({
          name: template.name,
          outputFields: (template.outputFields?.length || 0) + (template.outputMetafields?.length || 0),
          creditsPerProduct,
          totalCredits: templateCredits
        });
        
        console.log(`📋 Template "${template.name}": ${creditsPerProduct} credits per product (AI model: ${aiModelCost}, smart pricing applied) × ${products.length} products = ${templateCredits} credits`);
      }
      
      // Add product creation costs (1 credit per product)
      const productCreationCredits = products.length;
      const totalRequiredCreditsWithCreation = totalRequiredCredits + productCreationCredits;
      
      console.log(`💳 Template credits: ${totalRequiredCredits}`);
      console.log(`🏭 Product creation credits: ${productCreationCredits}`);
      console.log(`💰 Total required credits (templates + creation): ${totalRequiredCreditsWithCreation}`);
      console.log('📊 Template breakdown:', templateDetails);
      
      // Check if user has enough credits for both templates AND product creation
      const hasEnoughCredits = await creditService.hasEnoughCredits(user.uid, totalRequiredCreditsWithCreation);
      
      if (!hasEnoughCredits) {
        // Get user's current credit status for detailed error message
        const creditStatus = await creditService.getUserCreditStatus(user.uid);
        const availableCredits = (creditStatus?.monthly || 0) + (creditStatus?.flex || 0);
        const missingCredits = totalRequiredCredits - availableCredits;
        
        console.error('❌ Insufficient credits:', {
          required: totalRequiredCreditsWithCreation,
          available: availableCredits,
          missing: missingCredits
        });
        
        setLoading(false);
        
        // Return detailed error with purchase link suggestion
        return {
          error: 'INSUFFICIENT_CREDITS',
          details: {
            required: totalRequiredCreditsWithCreation,
            available: availableCredits,
            missing: missingCredits,
            message: `Not enough credits to execute templates and create products. You need ${totalRequiredCreditsWithCreation} credits (${totalRequiredCredits} for templates + ${productCreationCredits} for product creation) but only have ${availableCredits}. Missing: ${missingCredits} credits.`,
            purchaseUrl: '/app/credits' // Link to purchase page
          }
        };
      }
      
      console.log('✅ Credits check passed. Starting template execution...');
      
      // Call the V2 template execution system
      const results = await executeTemplatesV2(
        templateIds,
        products,
        onProgress,
        {
          shopId: currentShop?.id,
          userId: user?.uid,
          getFieldValue: (product, field) => getFieldValue(product, field),
          onComplete: (results) => {
            console.log('Template execution complete:', results);
            
            // Update the template results state
            setTemplateResults(prev => ({
              ...prev,
              ...results.results
            }));
          }
        }
      );
      
      setLoading(false);
      return results;
    } catch (error) {
      console.error('Error executing templates:', error);
      setLoading(false);
      return { error: error.message };
    }
  };

  const processTemplateResults = useCallback((results) => {
    console.log('Processing template results:', results);
    
    // Use the templateProcessingService to process the results
    return templateProcessingService.processTemplateResults(results, templates);
  }, [templates]);

  const getCurrentProductOptions = (productId, products, productEdits, editHistory) => {
    // Get options from all sources
    const editedOptions = productEdits[productId]?.options || [];
    const historyOptions = editHistory.present[productId]?.options || [];
    const originalOptions = products.find(p => p.id.toString() === productId.toString())?.options || [];

    // Create a merged options array
    const mergedOptions = [];
    const maxLength = Math.max(editedOptions.length, historyOptions.length, originalOptions.length);

    for (let i = 0; i < maxLength; i++) {
      // Get options from each source
      const edited = editedOptions[i] || {};
      const history = historyOptions[i] || {};
      const original = originalOptions[i] || {};

      // Merge in priority order: edited > history > original
      mergedOptions[i] = {
        id: edited.id || history.id || original.id,
        name: edited.name || history.name || original.name || '',
        position: edited.position || history.position || original.position || (i + 1),
        values: edited.values || history.values || original.values || []
      };
    }

    return mergedOptions;
  };

  const logOptionState = (label, productId, options) => {
    console.log(`\n=== ${label} for Product ${productId} ===`);
    options.forEach((opt, index) => {
      console.log(`Option ${index + 1}:`);
      console.log(`  Name: "${opt.name}"`);
      console.log(`  Values:`, opt.values);
    });
  };

  const registerHandleProductChange = useCallback((fn) => {
    handleProductChangeRef.current = fn;
  }, []);

  const updateProductField = useCallback((productId, field, value) => {
    if (handleProductChangeRef.current) {
      handleProductChangeRef.current(productId, field, value);
    }
  }, []);

  const registerHandleOptionValueChange = useCallback((fn) => {
    handleOptionValueChangeRef.current = fn;
  }, []);

  const registerHandleVariantChange = useCallback((handleVariantChangeFn) => {
    handleVariantChangeRef.current = handleVariantChangeFn;
    console.log('Registered handleVariantChange function');
  }, []);

  const duplicateSelectedTemplate = async (templateId) => {
    try {
      setLoading(true);
      const duplicatedTemplate = await templateService.duplicateTemplate(templateId, user.uid);
      setTemplates(prev => [...prev, duplicatedTemplate]);
    } catch (error) {
      console.error('Error duplicating template:', error);
    } finally {
      setLoading(false);
    }
  };

  const duplicateSelectedWorkflow = async (workflowId) => {
    try {
      setLoading(true);
      const duplicatedWorkflow = await templateService.duplicateWorkflow(workflowId, user.uid);
      setWorkflows(prev => [...prev, duplicatedWorkflow]);
    } catch (error) {
      console.error('Error duplicating workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeWorkflow = async (workflowOrId, products, onProgress = null, templateIds = null, options = {}) => {
    console.log('executeWorkflow called with:', { 
      workflowOrId: typeof workflowOrId === 'string' ? workflowOrId : 'workflow object', 
      productCount: products?.length,
      templateIds,
      stopMarkers: options.stopMarkers
    });
    
    if (!products || products.length === 0) {
      console.warn('No products provided to executeWorkflow');
      return { error: 'No products provided to executeWorkflow' };
    }
    
    if (!user?.uid) {
      console.error('User not authenticated');
      return { error: 'User not authenticated' };
    }
    
    try {
      setLoading(true);
      
      // Handle different input types
      let workflow;
      
      if (typeof workflowOrId === 'string') {
        // Case 1: String ID provided - look up the workflow
        if (workflowOrId === 'custom') {
          // Create a temporary workflow object for custom execution
          workflow = {
            id: 'custom',
            name: 'Custom Template Selection',
            templates: templateIds || [],
            userId: user.uid,
            createdAt: new Date().toISOString()
          };
          console.log('Executing custom workflow with templates:', templateIds);
        } else {
          // Get the workflow from the workflows array
          workflow = workflows.find(w => w.id === workflowOrId);
          if (!workflow) {
            throw new Error(`Workflow ${workflowOrId} not found`);
          }
          console.log('Executing workflow by ID:', workflow.name);
        }
      } else if (typeof workflowOrId === 'object' && workflowOrId !== null) {
        // Case 2: Workflow object provided - use it directly
        workflow = workflowOrId;
        console.log('Executing workflow object:', workflow.name);
      } else {
        throw new Error('Invalid workflow parameter: must be a workflow ID or workflow object');
      }
      
      // Use the templateIds parameter if provided, otherwise use the workflow's templates
      const templatesForExecution = templateIds || workflow.templates;
      
      if (!templatesForExecution || templatesForExecution.length === 0) {
        throw new Error('No templates selected for execution');
      }
      
      // If templateIds were provided and different from workflow.templates, update the workflow object
      if (templateIds && JSON.stringify(templateIds) !== JSON.stringify(workflow.templates)) {
        workflow = {
          ...workflow,
          templates: templateIds
        };
      }
      
      // CRITICAL: Pre-execution credits check for workflow
      console.log('🔍 Starting pre-execution credits check for workflow...');
      
      // Calculate total required credits based on workflow templates and products
      let totalRequiredCredits = 0;
      const templateDetails = [];
      
      for (const templateId of templatesForExecution) {
        const template = templates.find(t => t.id === templateId);
        if (!template) {
          console.error(`Template not found in workflow: ${templateId}`);
          setLoading(false);
          return { error: `Template not found in workflow: ${templateId}` };
        }
        
        // Calculate credits per template with smart field-based pricing
        const aiModelCost = template.creditCostPerOutput || 1; // Base cost from AI model
        const creditsPerProduct = calculateTemplateCredits(template, aiModelCost); // Smart calculation with field complexity
        const templateCredits = creditsPerProduct * products.length;
        
        totalRequiredCredits += templateCredits;
        templateDetails.push({
          name: template.name,
          outputFields: (template.outputFields?.length || 0) + (template.outputMetafields?.length || 0),
          creditsPerProduct,
          totalCredits: templateCredits
        });
        
        console.log(`📋 Workflow Template "${template.name}": ${creditsPerProduct} credits per product (AI model: ${aiModelCost}, smart pricing applied) × ${products.length} products = ${templateCredits} credits`);
      }
      
      console.log(`💳 Total required credits for workflow: ${totalRequiredCredits}`);
      console.log('📊 Workflow template breakdown:', templateDetails);
      
      // Check if user has enough credits
      const hasEnoughCredits = await creditService.hasEnoughCredits(user.uid, totalRequiredCredits);
      
      if (!hasEnoughCredits) {
        // Get user's current credit status for detailed error message
        const creditStatus = await creditService.getUserCreditStatus(user.uid);
        const availableCredits = (creditStatus?.monthly || 0) + (creditStatus?.flex || 0);
        const missingCredits = totalRequiredCredits - availableCredits;
        
        console.error('❌ Insufficient credits for workflow:', {
          required: totalRequiredCredits,
          available: availableCredits,
          missing: missingCredits,
          workflow: workflow.name
        });
        
        setLoading(false);
        
        // Return detailed error with purchase link suggestion
        return {
          error: 'INSUFFICIENT_CREDITS',
          details: {
            required: totalRequiredCredits,
            available: availableCredits,
            missing: missingCredits,
            message: `Not enough credits to execute workflow "${workflow.name}". You need ${totalRequiredCredits} credits but only have ${availableCredits}. Missing: ${missingCredits} credits.`,
            purchaseUrl: '/app/credits'
          }
        };
      }
      
      console.log('✅ Credits check passed for workflow. Starting execution...');
      
      // Execute the workflow using WorkflowServiceV2 - it returns executionId immediately
      const executionId = await WorkflowServiceV2.executeWorkflow(
        user.uid,
        workflow, // Pass the workflow object directly
        products,
        {
          shopId: currentShop?.id,
          stopMarkers: options.stopMarkers || [], // Pass stop markers to WorkflowServiceV2
          onProgress: (progressData) => {
            console.log(`Workflow progress: ${Math.round(progressData.progress)}%`);
            if (onProgress) {
              // Ensure progress is passed as percentage (0-100) to the UI
              const progressWithPercentage = {
                ...progressData,
                progress: progressData.progress // Already converted to percentage in workflowServiceV2.js
              };
              onProgress(progressWithPercentage);
            }
          },
          onTemplateComplete: (templateData) => {
            console.log(`Template ${templateData.templateId} completed, passing results to onProgress for UI processing`);
            // Pass template results through onProgress so TemplateTools can process them
            // This ensures body_html is properly mapped to descriptionHtml with insert mode
            if (onProgress) {
              onProgress({
                status: 'template_complete',
                templateId: templateData.templateId,
                templateIndex: templateData.templateIndex,
                results: templateData.results
              });
            }
          },
          onComplete: (completionData) => {
            console.log('Workflow execution complete');
            
            // Update template results in state
            setTemplateResults(prev => ({
              ...prev,
              ...completionData.results
            }));
            
            // IMPORTANT: Do NOT process results here!
            // TemplateTools.processTemplateResults already handles ALL result processing
            // This would cause DOUBLE/TRIPLE processing of body_html and other fields
            // We only update editHistory for undo/redo functionality
            console.log('%c[TemplateContext] Skipping result processing - already handled by TemplateTools.processTemplateResults', 'color: #00cc66; font-weight: bold');
            
            for (const [productId, templateResults] of Object.entries(completionData.results)) {
              for (const [templateId, result] of Object.entries(templateResults)) {
                // Update editHistory ONLY for undo/redo
                setEditHistory(prev => ({
                  past: prev.past || [],
                  present: {
                    ...prev.present,
                    [productId]: {
                      ...prev.present[productId],
                      ...result
                    }
                  },
                  future: prev.future || []
                }));
              }
            }
          },
          onError: (error) => {
            console.error('Workflow execution error:', error);
          }
        }
        );
      
      console.log('Workflow execution started with ID:', executionId);
      
      // Return executionId for resume functionality
      return { executionId };
    } catch (error) {
      console.error('Error executing workflow:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const executeTemplatesDirectly = async (templateIds, products, onProgress = null) => {
    console.log('%c[Direct Template Execution - Start]', 'color: #0099ff; font-weight: bold', { 
      templateIds: Array.isArray(templateIds) ? templateIds : [templateIds], 
      productCount: products?.length,
      productsHaveFullData: products?.length > 0 && typeof products[0] === 'object'
    });
    
    if (!templateIds || (Array.isArray(templateIds) && templateIds.length === 0)) {
      console.warn('%c[Direct Template Execution - No Templates]', 'color: #cc9900; font-weight: bold');
      return { error: 'No templates provided for execution' };
    }
    
    if (!products || products.length === 0) {
      console.warn('%c[Direct Template Execution - No Products]', 'color: #cc9900; font-weight: bold');
      return { error: 'No products provided for execution' };
    }
    
    if (!user?.uid) {
      console.error('User not authenticated');
      return { error: 'User not authenticated' };
    }
    
    try {
      // Normalize templateIds to array
      const templateIdArray = Array.isArray(templateIds) ? templateIds : [templateIds];
      
      // CRITICAL: Pre-execution credits check for direct template execution
      console.log('🔍 Direct Template Execution - Starting credits check...');
      
      // Calculate total required credits based on templates and products
      let totalRequiredCredits = 0;
      const templateDetails = [];
      
      for (const templateId of templateIdArray) {
        const template = templates.find(t => t.id === templateId);
        if (!template) {
          console.error(`Template not found: ${templateId}`);
          return { error: `Template not found: ${templateId}` };
        }
        
        // CRITICAL: Tool templates cost 0 credits - skip them in calculation
        if (template.type === 'tool') {
          console.log(`🛠️ Skipping tool template in credit calculation: "${template.name}"`);
          continue; // Skip this template, don't add to credits
        }
        
        // Calculate credits per template with smart field-based pricing
        const aiModelCost = template.creditCostPerOutput || 1; // Base cost from AI model
        const creditsPerProduct = calculateTemplateCredits(template, aiModelCost); // Smart calculation with field complexity
        const templateCredits = creditsPerProduct * products.length;
        
        totalRequiredCredits += templateCredits;
        templateDetails.push({
          name: template.name,
          outputFields: (template.outputFields?.length || 0) + (template.outputMetafields?.length || 0),
          creditsPerProduct,
          totalCredits: templateCredits
        });
        
        console.log(`📋 Template "${template.name}": ${creditsPerProduct} credits per product (AI model: ${aiModelCost}, smart pricing applied) × ${products.length} products = ${templateCredits} credits`);
      }
      
      console.log(`💳 Total required credits for direct execution: ${totalRequiredCredits}`);
      console.log('📊 Template breakdown:', templateDetails);
      
      // Check if user has enough credits
      const hasEnoughCredits = await creditService.hasEnoughCredits(user.uid, totalRequiredCredits);
      
      if (!hasEnoughCredits) {
        // Get user's current credit status for detailed error message
        const creditStatus = await creditService.getUserCreditStatus(user.uid);
        // CRITICAL: Credits are nested under creditStatus.credits, not directly accessible
        const availableCredits = (creditStatus?.credits?.monthly || 0) + (creditStatus?.credits?.flex || 0);
        const missingCredits = totalRequiredCredits - availableCredits;
        
        console.error('❌ Insufficient credits for direct execution:', {
          required: totalRequiredCredits,
          available: availableCredits,
          missing: missingCredits,
          creditStatus: creditStatus?.credits // Debug: Show actual credit structure
        });
        
        // Return detailed error with purchase link suggestion
        return {
          error: 'INSUFFICIENT_CREDITS',
          details: {
            required: totalRequiredCredits,
            available: availableCredits,
            missing: missingCredits,
            message: `Not enough credits to execute templates. You need ${totalRequiredCredits} credits but only have ${availableCredits}. Missing: ${missingCredits} credits.`,
            purchaseUrl: '/app/credits'
          }
        };
      }
      
      console.log('✅ Credits check passed for direct execution. Proceeding...');
      
      
      // Find the first image template in the workflow (respecting order)
      let firstImageTemplateIndex = -1;
      let firstImageTemplate = null;
      
      for (let i = 0; i < templateIdArray.length; i++) {
        const template = templates.find(t => t.id === templateIdArray[i]);
        if (template && template.type === 'image') {
          firstImageTemplateIndex = i;
          firstImageTemplate = template;
          break;
        }
      }
      
      // Log the template breakdown
      console.log('%c[Direct Template Execution - Template Breakdown]', 'color: #0099ff; font-weight: bold', {
        totalTemplates: templateIdArray.length,
        firstImageTemplateIndex: firstImageTemplateIndex,
        hasImageTemplate: firstImageTemplate !== null
      });
      
      // Process templates before the first image template (if any)
      let previousTemplatesResults = null;
      if (firstImageTemplateIndex > 0) {
        // Get templates before the image template (respecting order)
        const previousTemplateIds = templateIdArray.slice(0, firstImageTemplateIndex);
        
        console.log('%c[Direct Template Execution - Processing Templates Before Image Template]', 'color: #0099ff; font-weight: bold', {
          templateCount: previousTemplateIds.length,
          templateIds: previousTemplateIds
        });
        
        // Create a temporary workflow for previous templates
        const previousTempWorkflow = {
          id: 'temp-previous-' + Date.now(),
          name: 'Templates Before Image Template',
          templates: previousTemplateIds,
          userId: user?.uid,
          createdAt: new Date().toISOString()
        };
        
        // Execute the previous templates workflow with corrected progress context
        // NOTE: These templates are executed AFTER the image template, but should appear as if executed BEFORE
        const wrappedProgressCallback = onProgress ? (progressData) => {
          // Modify progress data to show correct template position in overall workflow
          const modifiedProgressData = {
            ...progressData,
            message: progressData.message ? 
              progressData.message.replace(
                /Template (\d+)\/(\d+):/,
                (match, current, total) => {
                  // Show the correct position in the full workflow (these are the templates before the image template)
                  const actualCurrent = parseInt(current);
                  const actualTotal = templateIdArray.length;
                  return `Template ${actualCurrent}/${actualTotal}:`;
                }
              ) : progressData.message,
            // Adjust progress to account for the image template that was already "processed"
            progress: progressData.progress ? 
              ((firstImageTemplateIndex / templateIdArray.length) * 100) + 
              (progressData.progress * (firstImageTemplateIndex / templateIdArray.length)) 
              : progressData.progress
          };
          onProgress(modifiedProgressData);
        } : null;
        
        previousTemplatesResults = await executeWorkflow(previousTempWorkflow, products, wrappedProgressCallback);
        
        console.log('%c[Direct Template Execution - Previous Templates Complete]', 'color: #0099ff; font-weight: bold', {
          resultsSize: JSON.stringify(previousTemplatesResults).length,
          productCount: Object.keys(previousTemplatesResults).length,
          success: Object.keys(previousTemplatesResults).length > 0
        });
      }
      
      // If we found an image template, return a special response
      if (firstImageTemplate !== null) {
        console.log('%c[Direct Template Execution - Image Template Detected]', 'color: #ff9900; font-weight: bold', {
          imageTemplate: firstImageTemplate.name,
          position: firstImageTemplateIndex,
          totalTemplates: templateIdArray.length,
          previousTemplatesProcessed: firstImageTemplateIndex > 0
        });
        
        // Calculate remaining templates after this image template
        const remainingTemplateIds = firstImageTemplateIndex < templateIdArray.length - 1 ? 
          templateIdArray.slice(firstImageTemplateIndex + 1) : [];
        
        // Return a special response that signals to show the image template executor
        // Include the previous template results and remaining templates
        return {
          imageTemplate: firstImageTemplate,
          products: products,
          requiresImageSelection: true,
          previousTemplatesResults: previousTemplatesResults, // Include results from templates before this one
          remainingTemplateIds: remainingTemplateIds, // Include templates that should be processed after this one
          templatePosition: {
            current: firstImageTemplateIndex,
            total: templateIdArray.length
          }
        };
      }
      
      // Create a temporary workflow object
      const tempWorkflow = {
        id: 'temp-' + Date.now(),
        name: 'Direct Template Execution',
        templates: templateIdArray,
        userId: user?.uid,
        createdAt: new Date().toISOString()
      };
      
      console.log('%c[Direct Template Execution - Created Temp Workflow]', 'color: #0099ff; font-weight: bold', {
        workflowId: tempWorkflow.id,
        templates: tempWorkflow.templates,
        userId: tempWorkflow.userId
      });
      
      // Execute the temporary workflow with full product objects
      const results = await executeWorkflow(tempWorkflow, products, onProgress);
      
      console.log('%c[Direct Template Execution - Complete]', 'color: #0099ff; font-weight: bold', {
        resultsSize: JSON.stringify(results).length,
        productCount: Object.keys(results).length,
        success: Object.keys(results).length > 0
      });
      
      return results;
    } catch (error) {
      console.error('%c[Direct Template Execution - Error]', 'color: #cc0000; font-weight: bold', {
        error: error.message,
        stack: error.stack
      });
      
      // Return a structured error response
      return {
        error: `Error executing templates directly: ${error.message}`,
        details: error.stack
      };
    }
  };

  const value = {
    templates,
    workflows,
    loading,
    templateResults,
    aiModels,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    addWorkflow,
    updateWorkflow,
    deleteWorkflow,
    executeTemplate,
    executeTemplates,
    executeWorkflow,
    executeTemplatesDirectly,
    registerHandleProductChange,
    registerHandleOptionValueChange,
    registerHandleVariantChange,
    productEdits,
    editHistory,
    setEditHistory,
    products,
    setProducts,
    setProductEdits,
    getCurrentProductOptions,
    duplicateSelectedTemplate,
    duplicateSelectedWorkflow
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}
